import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileText, Loader2, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { createInvoice, getInvoice, invoicePdfUrl, api } from "@/lib/api";

const initialBuyer = {
  first_name: "",
  last_name: "",
  email: "",
  company: "",
  vat_id: "",
  street: "",
  zip: "",
  city: "",
  country: "Deutschland",
  accept_immediate_delivery: true,
  accept_privacy: false,
};

export default function Invoice() {
  const { id } = useParams();
  const nav = useNavigate();
  const [buyer, setBuyer] = useState(initialBuyer);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null); // {number, date, amounts}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const inv = await getInvoice(id);
        if (!alive) return;
        setExisting(inv);
      } catch (e) {
        // 404 = no invoice yet → show form (expected)
        if (e?.response?.status === 402) {
          toast.error("Zahlung fehlt – zurück zur Vorschau.");
          nav(`/result/${id}`);
          return;
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, nav]);

  const set = (k, v) => setBuyer((b) => ({ ...b, [k]: v }));

  const validate = () => {
    if (!buyer.first_name.trim() || !buyer.last_name.trim()) return "Bitte Vor- und Nachname ausfüllen.";
    if (!buyer.email.trim() || !buyer.email.includes("@")) return "Bitte gültige E-Mail-Adresse angeben.";
    if (!buyer.accept_privacy) return "Bitte Datenschutz-Verarbeitung bestätigen.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.warning(err); return; }
    setSubmitting(true);
    try {
      const res = await createInvoice(id, buyer);
      toast.success(`Rechnung ${res.invoice_number} erstellt.`);
      nav(`/report/${id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Rechnung konnte nicht erstellt werden.");
    } finally {
      setSubmitting(false);
    }
  };

  const skipForNow = () => {
    // Allow continuing without invoice (will still be available later from /report)
    nav(`/report/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#10C98D" }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200/70 bg-white/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" />
          <div className="text-xs text-slate-500">Schritt 2 von 2 · Rechnungsdaten</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-100 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#10C98D" }} />
          <div className="text-sm text-slate-700">
            <b>Zahlung bestätigt.</b> Bitte vervollständige deine <b>Rechnungsdaten</b> für die § 14 UStG-konforme Rechnung. Du erhältst die Rechnung danach direkt als PDF und kannst zur KI-Erstbewertung weitergehen.
          </div>
        </div>

        {existing ? (
          <ExistingInvoiceCard inv={existing} id={id} onContinue={() => nav(`/report/${id}`)} />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-8">
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              Rechnungsdaten
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              Pflichtfelder sind mit * markiert. Adresse + Firma sind <i>optional</i>, aber empfohlen für Geschäftskunden.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Field label="Vorname *" testid="invoice-firstname" value={buyer.first_name} onChange={(v) => set("first_name", v)} />
              <Field label="Nachname *" testid="invoice-lastname" value={buyer.last_name} onChange={(v) => set("last_name", v)} />
              <Field label="E-Mail *" testid="invoice-email" value={buyer.email} onChange={(v) => set("email", v)} type="email" colspan="sm:col-span-2" />
              <Field label="Firma (optional)" testid="invoice-company" value={buyer.company} onChange={(v) => set("company", v)} colspan="sm:col-span-2" />
              <Field label="USt-IdNr. (optional, für B2B)" testid="invoice-vatid" value={buyer.vat_id} onChange={(v) => set("vat_id", v)} colspan="sm:col-span-2" />
              <Field label="Straße + Hausnummer (optional)" testid="invoice-street" value={buyer.street} onChange={(v) => set("street", v)} colspan="sm:col-span-2" />
              <Field label="PLZ (optional)" testid="invoice-zip" value={buyer.zip} onChange={(v) => set("zip", v)} />
              <Field label="Ort (optional)" testid="invoice-city" value={buyer.city} onChange={(v) => set("city", v)} />
              <Field label="Land" testid="invoice-country" value={buyer.country} onChange={(v) => set("country", v)} colspan="sm:col-span-2" />
            </div>

            <div className="mt-6 space-y-3">
              <CheckboxRow
                checked={buyer.accept_privacy}
                onChange={(v) => set("accept_privacy", v)}
                testid="invoice-accept-privacy"
                text={<>Ich willige in die Verarbeitung meiner Rechnungsdaten zur Erstellung und Aufbewahrung der Rechnung gemäß <b>§ 14b UStG</b> ein (gesetzliche Aufbewahrung 10 Jahre). Weitere Infos in der Datenschutzerklärung. *</>}
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={skipForNow}
                data-testid="invoice-skip-btn"
                className="text-slate-600"
              >
                Später ausfüllen
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="invoice-submit-btn"
                className="h-12 px-7 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird erstellt…</>
                ) : (
                  <><FileText className="h-4 w-4 mr-2" /> Rechnung erstellen & weiter</>
                )}
              </Button>
            </div>

            <div className="mt-6 flex items-start gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#10C98D" }} />
              <span>
                Deine Daten werden ausschließlich zur Rechnungserstellung und gesetzlichen Aufbewahrung verarbeitet.
                Sie werden nicht für Werbung verwendet und nicht an Dritte verkauft.
              </span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", colspan = "" }) {
  return (
    <div className={colspan}>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label>
      <Input
        type={type}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-11"
      />
    </div>
  );
}

function CheckboxRow({ checked, onChange, text, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-50 transition-colors">
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        data-testid={testid}
        className="mt-0.5"
      />
      <span className="text-sm text-slate-700 leading-relaxed">{text}</span>
    </label>
  );
}

function ExistingInvoiceCard({ inv, id, onContinue }) {
  const fmtAmount = (amounts, currency) => {
    const v = amounts.gross.toFixed(2);
    if (currency === "EUR") return v.replace(".", ",") + " €";
    if (currency === "USD") return "$" + v;
    return `${v} ${currency}`;
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-8">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 mb-3 font-semibold">
        <CheckCircle2 className="h-4 w-4" /> Rechnung bereits erstellt
      </div>
      <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
        Rechnung Nr. {inv.number}
      </h1>
      <div className="text-sm text-slate-600 mt-1">
        Ausgestellt am {inv.date} · Betrag: {fmtAmount(inv.amounts, inv.currency)}
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Button
          asChild
          data-testid="invoice-download-btn"
          className="h-12 px-6 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95"
        >
          <a href={invoicePdfUrl(id)} target="_blank" rel="noreferrer">
            <Download className="h-4 w-4 mr-2" /> Rechnung als PDF
          </a>
        </Button>
        <Button
          onClick={onContinue}
          variant="outline"
          data-testid="invoice-continue-btn"
          className="h-12 px-6 rounded-xl"
        >
          Weiter zur KI-Erstbewertung <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
